import React, { Suspense, useMemo } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import {
  APP_NAV_ITEMS,
  canAccessNavItem,
  getAccessibleNavItems,
} from '../navigation/appNavigation';
import PrivateRoute from './PrivateRoute';
import AppShell from '../components/AppShell';
import { NON_NAV_SHELL_PATHS } from './shellRoutes';
import { FORM_FULL_PAGE_ROUTES } from '../forms/registry';

/** Path pattern → the registered form behind it, for `renderNonNavShellRoute`. */
const FORM_ROUTE_BY_PATH = new Map(FORM_FULL_PAGE_ROUTES.map((route) => [route.path, route]));

const LoginPage = React.lazy(() => import('../pages/LoginPage'));
const ForgotPasswordPage = React.lazy(() => import('../pages/ForgotPasswordPage'));
const ResetPasswordPage = React.lazy(() => import('../pages/ResetPasswordPage'));
const VerifyEmailPage = React.lazy(() => import('../pages/VerifyEmailPage'));
const DashboardPage = React.lazy(() => import('../pages/DashboardPage'));
const OauthClientsPage = React.lazy(() => import('../pages/OauthClientsPage'));
const OauthUsersPage = React.lazy(() => import('../pages/OauthUsersPage'));
const UploadS3PublicShareTokenPage = React.lazy(() => import('../pages/UploadS3PublicShareTokenPage'));
const ExampleBatchUploadPage = React.lazy(() => import('../pages/ExampleBatchUploadPage'));
// CRM core (Rules 65 / 75 / 76).
const OrganizationsPage = React.lazy(() => import('../pages/OrganizationsPage'));
const ContactsPage = React.lazy(() => import('../pages/ContactsPage'));
const TagsPage = React.lazy(() => import('../pages/TagsPage'));
const LeadsPage = React.lazy(() => import('../pages/LeadsPage'));
const PipelinePage = React.lazy(() => import('../pages/PipelinePage'));
const OpportunitiesPage = React.lazy(() => import('../pages/OpportunitiesPage'));
// Phase 6 — Customer 360°. Param routes, so they are registered standalone
// below rather than iterated from APP_NAV_ITEMS.
const ContactDetailPage = React.lazy(() => import('../pages/ContactDetailPage'));
const OrganizationDetailPage = React.lazy(() => import('../pages/OrganizationDetailPage'));
const LeadDetailPage = React.lazy(() => import('../pages/LeadDetailPage'));
const OpportunityDetailPage = React.lazy(() => import('../pages/OpportunityDetailPage'));
const LiveChatPage = React.lazy(() => import('../pages/LiveChatPage'));
const MyDayPage = React.lazy(() => import('../pages/MyDayPage'));
const ActivitiesPage = React.lazy(() => import('../pages/ActivitiesPage'));
const SalesTeamsPage = React.lazy(() => import('../pages/SalesTeamsPage'));
const AssignmentRulesPage = React.lazy(() => import('../pages/AssignmentRulesPage'));
const PipelineSettingsPage = React.lazy(() => import('../pages/PipelineSettingsPage'));
const ChatPage = React.lazy(() => import('../pages/ChatPage'));
const AuditLogsPage = React.lazy(() => import('../pages/AuditLogsPage'));
const OutboundHttpLogsPage = React.lazy(() => import('../pages/OutboundHttpLogsPage'));
const FieldHistoryPage = React.lazy(() => import('../pages/FieldHistoryPage'));
const LoginAttemptsPage = React.lazy(() => import('../pages/LoginAttemptsPage'));
const ChatMessagesAdminPage = React.lazy(() => import('../pages/ChatMessagesAdminPage'));
const WebhookDeliveriesPage = React.lazy(() => import('../pages/WebhookDeliveriesPage'));
const FailedJobsPage = React.lazy(() => import('../pages/FailedJobsPage'));
const SystemSettingsPage = React.lazy(() => import('../pages/SystemSettingsPage'));
const ProfilePage = React.lazy(() => import('../pages/ProfilePage'));
const SessionsPage = React.lazy(() => import('../pages/SessionsPage'));
const ChangePasswordPage = React.lazy(() => import('../pages/ChangePasswordPage'));
const MfaSettingsPage = React.lazy(() => import('../pages/MfaSettingsPage'));
// Rule 73 — Authorized Device account-security landing.
const AccountSecurityPage = React.lazy(() => import('../pages/AccountSecurityPage'));
// Rule 70 — Federated identity (Sign in with Google / Facebook).
const OAuthCallbackPage = React.lazy(() => import('../pages/OAuthCallbackPage'));
const CompleteProfilePage = React.lazy(() => import('../pages/CompleteProfilePage'));
const LinkedAccountsPage = React.lazy(() => import('../pages/LinkedAccountsPage'));
// Rule 74 — Voice + Video Calls.
const CallPage = React.lazy(() => import('../pages/CallPage'));
const CallHistoryPage = React.lazy(() => import('../pages/CallHistoryPage'));
// The page surface of every registered Add/Edit form — one component behind
// every `fullPagePath` in `forms/registry.js`.
const FormFullPage = React.lazy(() => import('../pages/FormFullPage'));
// const ModulePage = React.lazy(() => import('../pages/ModulePage'));

function RouteFallback() {
  const { t } = useTranslation();

  return (
    <div
      role="status"
      aria-live="polite"
      className="flex h-full min-h-[12rem] items-center justify-center text-sm text-gray-500"
    >
      {t('app.loading')}
    </div>
  );
}

function withRouteSuspense(element) {
  return <Suspense fallback={<RouteFallback />}>{element}</Suspense>;
}

/**
 * The element behind each `NON_NAV_SHELL_PATHS` entry. Keyed by the very same
 * pattern string AppShell matches against, so a path can never be registered
 * here without the shell also recognising it (the drift that made every
 * Customer 360° record page report "Page not available for your account").
 */
function renderNonNavShellRoute(path) {
  // Add/Edit form pages resolve from the registry, so adding a form never
  // means touching this switch.
  const formRoute = FORM_ROUTE_BY_PATH.get(path);
  if (formRoute) {
    return withRouteSuspense(
      <FormFullPage formKey={formRoute.formKey} paramKey={formRoute.paramKey} />,
    );
  }

  switch (path) {
    case '/profile':                  return withRouteSuspense(<ProfilePage />);
    case '/profile/sessions':         return withRouteSuspense(<SessionsPage />);
    case '/profile/change-password':  return withRouteSuspense(<ChangePasswordPage />);
    case '/profile/mfa':              return withRouteSuspense(<MfaSettingsPage />);
    case '/profile/account-security': return withRouteSuspense(<AccountSecurityPage />);
    case '/profile/linked-accounts':  return withRouteSuspense(<LinkedAccountsPage />);
    case '/profile/complete':         return withRouteSuspense(<CompleteProfilePage />);
    // Phase 6 — Customer 360° record pages. Reached from the list rows, so
    // they carry no nav entry of their own.
    case '/contacts/:contactId':               return withRouteSuspense(<ContactDetailPage />);
    case '/organizations/:organizationId':     return withRouteSuspense(<OrganizationDetailPage />);
    case '/leads/:leadId':                     return withRouteSuspense(<LeadDetailPage />);
    case '/opportunities/:opportunityId':      return withRouteSuspense(<OpportunityDetailPage />);
    // Rule 74 — Voice + Video Call routes. You only land here via
    // start/accept-call, never from the sidebar.
    case '/call/:callId':             return withRouteSuspense(<CallPage />);
    case '/threads/:threadId/calls':  return withRouteSuspense(<CallHistoryPage />);
    default:                          return null;
  }
}

function renderModuleRoute(item) {
  if (item.path === '/dashboard') return withRouteSuspense(<DashboardPage />);
  if (item.path === '/admin/users') return withRouteSuspense(<OauthUsersPage />);
  if (item.path === '/admin/oauth-clients') return withRouteSuspense(<OauthClientsPage />);
  if (item.path === '/admin/upload-s3-public-share-tokens') {
    return withRouteSuspense(<UploadS3PublicShareTokenPage />);
  }
  if (item.path === '/example-batch-upload') return withRouteSuspense(<ExampleBatchUploadPage />);
  if (item.path === '/organizations') return withRouteSuspense(<OrganizationsPage />);
  if (item.path === '/contacts') return withRouteSuspense(<ContactsPage />);
  if (item.path === '/tags') return withRouteSuspense(<TagsPage />);
  if (item.path === '/leads') return withRouteSuspense(<LeadsPage />);
  if (item.path === '/pipeline') return withRouteSuspense(<PipelinePage />);
  if (item.path === '/opportunities') return withRouteSuspense(<OpportunitiesPage />);
  if (item.path === '/my-day') return withRouteSuspense(<MyDayPage />);
  if (item.path === '/activities') return withRouteSuspense(<ActivitiesPage />);
  if (item.path === '/sales-teams') return withRouteSuspense(<SalesTeamsPage />);
  if (item.path === '/assignment-rules') return withRouteSuspense(<AssignmentRulesPage />);
  if (item.path === '/pipeline-settings') return withRouteSuspense(<PipelineSettingsPage />);
  if (item.path === '/livechat') return withRouteSuspense(<LiveChatPage />);
  if (item.path === '/chat') return withRouteSuspense(<ChatPage />);
  if (item.path === '/admin/audit-logs') return withRouteSuspense(<AuditLogsPage />);
  if (item.path === '/admin/outbound-http-logs') return withRouteSuspense(<OutboundHttpLogsPage />);
  if (item.path === '/admin/field-history') return withRouteSuspense(<FieldHistoryPage />);
  if (item.path === '/admin/login-attempts') return withRouteSuspense(<LoginAttemptsPage />);
  if (item.path === '/admin/chat-messages') return withRouteSuspense(<ChatMessagesAdminPage />);
  if (item.path === '/admin/webhook-deliveries') return withRouteSuspense(<WebhookDeliveriesPage />);
  if (item.path === '/admin/failed-jobs') return withRouteSuspense(<FailedJobsPage />);
  if (item.path === '/admin/system-settings') return withRouteSuspense(<SystemSettingsPage />);
  // if (item.path === '/{path}') return withRouteSuspense(<ModulePage />);

  return (
    <div className="flex h-full flex-col items-center justify-center gap-2 text-gray-400">
      <p className="text-lg font-semibold">{item.label}</p>
      <p className="text-sm">{item.description}</p>
    </div>
  );
}

export default function AppRoutes() {
  const { isAuthenticated, user } = useAuth();
  const userScopes = useMemo(
    () => (Array.isArray(user?.scopes) ? user.scopes : []),
    [user?.scopes],
  );
  const fallbackPath = useMemo(
    () => getAccessibleNavItems(userScopes)[0]?.path ?? '/dashboard',
    [userScopes],
  );

  return (
    <Routes>
      <Route
        path="/"
        element={<Navigate to={isAuthenticated ? fallbackPath : '/login'} replace />}
      />

      <Route path="/login" element={withRouteSuspense(<LoginPage />)} />
      <Route path="/forgot-password" element={withRouteSuspense(<ForgotPasswordPage />)} />
      <Route path="/reset-password" element={withRouteSuspense(<ResetPasswordPage />)} />
      <Route path="/verify-email" element={withRouteSuspense(<VerifyEmailPage />)} />
      {/* Rule 70 — federated callback. Public; the page reads the URL fragment
          and POSTs the one-shot handoff code to /auth/identities/exchange. */}
      <Route path="/oauth/callback" element={withRouteSuspense(<OAuthCallbackPage />)} />

      <Route element={<PrivateRoute />}>
        <Route element={<AppShell />}>
          {APP_NAV_ITEMS.map((item) => (
            <Route
              key={item.path}
              path={item.path}
              element={
                canAccessNavItem(userScopes, item)
                  ? renderModuleRoute(item)
                  : <Navigate to={fallbackPath} replace />
              }
            />
          ))}
          {/* Non-nav in-shell routes — profile, Customer 360° record pages and
              the call routes. Driven by NON_NAV_SHELL_PATHS so this list and
              AppShell's "is this a real destination?" check can never drift
              apart again. */}
          {NON_NAV_SHELL_PATHS.map((path) => (
            <Route key={path} path={path} element={renderNonNavShellRoute(path)} />
          ))}
        </Route>
      </Route>

      <Route
        path="*"
        element={<Navigate to={isAuthenticated ? fallbackPath : '/login'} replace />}
      />
    </Routes>
  );
}
